/**
 * noiseFloor — how much two watches on the SAME body disagree about a stroke.
 *
 * Steering-committee finding (2026-09-01): a trend verdict at a flat 1 s
 * threshold that nobody measured is a claim about the swimmer that may only
 * be a claim about the watch. The watch noise floor IS measurable from
 * fixtures on disk: four swims were recorded by two watches at once
 * (fixtures/swim-firehose-2026-06-07/08/11 vs their _wrist2 twins, and
 * 2026-05-27 vs _dad — Scott wearing his dad's watch, same body).
 *
 * `crossWatchNoise` takes those pairs and, per stroke, reports the median
 * absolute difference between the two watches' per-SWIM stroke medians
 * (record-eligible lengths, 25 m only, the same numbers the Your-strokes
 * cards draw). `trendFloorFor` turns a measurement into the threshold a
 * trend must clear: max(1.0 s, 1.5 × measured), rounded to 0.5 s; with fewer
 * than two usable pairs for a stroke, a conservative 3.0 s.
 *
 * TREND_FLOOR_S pins the result so the app never re-derives it at runtime;
 * noiseFloor.test.ts re-measures the fixtures and asserts the pins still
 * hold, so the numbers stay documented and re-derivable.
 */

/** Structural subset of a swimFacts entry the measurement reads. */
export interface NoiseSource {
  poolLengthMeters: number | null;
  strokes: Record<string, { n: number; medianS: number }> | null;
}

export interface NoisePair {
  a: NoiseSource;
  b: NoiseSource;
}

export interface StrokeNoise {
  /** Pairs where BOTH watches had ≥ minLengths eligible lengths of the stroke. */
  pairs: number;
  /** Median |medianS(a) − medianS(b)| across those pairs, seconds per 25. */
  medianAbsDiffS: number;
  /** Every pair's absolute difference, for the test's printout. */
  diffsS: number[];
}

/** A pair only speaks for a stroke when both watches saw a real sample of it. */
export const NOISE_MIN_LENGTHS = 6;
/** Fewer usable pairs than this → the conservative floor. */
export const NOISE_MIN_PAIRS = 2;
export const NOISE_SAFETY_FACTOR = 1.5;
export const TREND_FLOOR_MIN_S = 1.0;
export const TREND_FLOOR_UNMEASURED_S = 3.0;

/**
 * Measured on the four cross-watch fixture pairs (see noiseFloor.test.ts for
 * the per-pair numbers). Strokes with fewer than two usable pairs take
 * TREND_FLOOR_UNMEASURED_S and say so below.
 */
export const TREND_FLOOR_S: Record<string, number> = {
  // UNMEASURED: no pair had 6+ fly lengths on both watches (2, 0, 4, 5) — the
  // conservative floor. The 2–5-length pairs disagreed by 4.6 / 3.9 / 0.2 s, so
  // 3.0 s is not generous; re-measure when a two-watch swim carries a fly set.
  Butterfly: 3.0,
  // 2 pairs (Jun 8: 3.63 s, May 27: 3.06 s) → median 3.34 → × 1.5 = 5.02 → 5.0
  Backstroke: 5.0,
  // 4 pairs (1.67, 1.77, 0.07, 1.16 s) → median 1.42 → × 1.5 = 2.13 → 2.0
  Breaststroke: 2.0,
  // 4 pairs (0.45, 0.00, 0.63, 0.42 s) → median 0.43 → × 1.5 = 0.65 → min 1.0
  Freestyle: 1.0,
};

/** Which pins came from ≥ 2 usable pairs (true) vs the unmeasured fallback. */
export const TREND_FLOOR_MEASURED: Record<string, boolean> = {
  Butterfly: false,
  Backstroke: true,
  Breaststroke: true,
  Freestyle: true,
};

/** The threshold a stroke's trend must clear; the unmeasured floor for a stroke we never paired. */
export function trendFloorS(stroke: string): number {
  return TREND_FLOOR_S[stroke] ?? TREND_FLOOR_UNMEASURED_S;
}

/** False when the floor is the fallback, so copy can say it is a guess. */
export function isTrendFloorMeasured(stroke: string): boolean {
  return TREND_FLOOR_MEASURED[stroke] ?? false;
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Per stroke, the median absolute cross-watch difference of per-swim medians. */
export function crossWatchNoise(
  pairs: NoisePair[],
  minLengths: number = NOISE_MIN_LENGTHS,
): Record<string, StrokeNoise> {
  const diffs = new Map<string, number[]>();
  for (const { a, b } of pairs) {
    if (a.poolLengthMeters !== 25 || b.poolLengthMeters !== 25) continue;
    if (a.strokes === null || b.strokes === null) continue;
    for (const [stroke, sa] of Object.entries(a.strokes)) {
      const sb = b.strokes[stroke];
      if (!sb || sa.n < minLengths || sb.n < minLengths) continue;
      const list = diffs.get(stroke) ?? [];
      list.push(Math.abs(sa.medianS - sb.medianS));
      diffs.set(stroke, list);
    }
  }
  const out: Record<string, StrokeNoise> = {};
  for (const [stroke, list] of diffs) {
    out[stroke] = { pairs: list.length, medianAbsDiffS: median(list), diffsS: list };
  }
  return out;
}

/** max(1.0, 1.5 × measured) rounded to 0.5 s; 3.0 s with fewer than two usable pairs. */
export function trendFloorFor(noise: StrokeNoise | undefined): number {
  if (!noise || noise.pairs < NOISE_MIN_PAIRS) return TREND_FLOOR_UNMEASURED_S;
  const raw = Math.max(TREND_FLOOR_MIN_S, NOISE_SAFETY_FACTOR * noise.medianAbsDiffS);
  return Math.round(raw * 2) / 2;
}
